import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEye, 
  faCheck, 
  faClock, 
  faTimes,
  faExclamationTriangle,
  faExclamationCircle,
  faInfoCircle,
  faCheckCircle,
  faChevronUp,
  faChevronDown,
  faCalendar
} from '@fortawesome/free-solid-svg-icons';
import { ListGroup, Button, Badge, Collapse } from '@themesberg/react-bootstrap';
import { getNotificationConfig, formatNotificationTime, getAvailableActions } from '../utils/notificationUtils';
import { NOTIFICATION_ACTIONS } from '../constants/notificationTypes';

// Map string icons to FontAwesome icons
const getIconComponent = (iconName) => {
  const iconMap = {
    'exclamation-triangle': faExclamationTriangle,
    'exclamation-circle': faExclamationCircle,
    'info-circle': faInfoCircle,
    'check-circle': faCheckCircle,
    'eye': faEye,
    'check': faCheck,
    'clock': faClock,
    'calendar': faCalendar,
    'times': faTimes,
    'chevron-up': faChevronUp,
    'chevron-down': faChevronDown
  };
  return iconMap[iconName] || faInfoCircle;
};

const NotificationItem = ({ 
  notification, 
  onAction, 
  onMarkAsRead 
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showGroupItems, setShowGroupItems] = useState(false);
  
  const config = getNotificationConfig(notification);
  const availableActions = getAvailableActions(notification);
  const isUnread = notification.status !== 'read';

  const handleAction = (actionType) => {
    onAction(actionType, notification);
    setShowActions(false);
  };

  const handleClick = () => {
    if (isUnread) {
      onMarkAsRead(notification.id);
    }
    setShowActions(!showActions);
  };

  const toggleGroupItems = (e) => {
    e.stopPropagation();
    setShowGroupItems(!showGroupItems);
  };

  return (
    <>
      <ListGroup.Item
        action
        className={`border-bottom border-light notification-item ${isUnread ? 'notification-unread' : ''}`}
        onClick={handleClick}
        style={{
          borderLeft: `4px solid ${config.color}`,
          backgroundColor: isUnread ? config.backgroundColor : 'transparent',
          cursor: 'pointer'
        }}
      >
        <div className="d-flex align-items-start">
          {/* Notification Icon */}
          <div 
            className="flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{ 
              width: 36, 
              height: 36, 
              backgroundColor: config.color,
              color: 'white'
            }}
          >
            <FontAwesomeIcon 
              icon={getIconComponent(config.icon)} 
              size="sm"
            />
          </div>
          
          {/* Notification Content */}
          <div className="flex-1">
            <div className="d-flex justify-content-between align-items-start mb-1">
              <div className="d-flex align-items-center">
                <h6 className={`mb-0 ${isUnread ? 'fw-bold' : 'fw-normal'}`} style={{ color: config.color }}>
                  {notification.notification_type}
                </h6>
                
                {/* Group Badge */}
                {notification.isGroup && (
                  <Badge bg="secondary" className="ms-2 small">
                    {notification.count} items
                  </Badge>
                )}
                
                {/* Priority Badge */}
                {notification.priority === 'URGENT' && (
                  <Badge bg="danger" className="ms-2 small">
                    Urgent
                  </Badge>
                )}
              </div>
              
              <div className="d-flex align-items-center">
                <small className={isUnread ? 'text-danger fw-bold' : 'text-muted'}>
                  {formatNotificationTime(notification.created_at)}
                </small>
                
                {/* Group Expand Button */}
                {notification.isGroup && (
                  <Button
                    variant="link"
                    size="sm"
                    className="ms-2 p-0"
                    onClick={toggleGroupItems}
                  >
                    <FontAwesomeIcon 
                      icon={showGroupItems ? faChevronUp : faChevronDown} 
                      size="sm"
                    />
                  </Button>
                )}
              </div>
            </div>
            
            <p className="mb-1 small text-dark">
              {notification.message}
            </p>
            
            {/* Unread Indicator */}
            {isUnread && (
              <div className="d-flex align-items-center">
                <div 
                  className="rounded-circle me-2"
                  style={{
                    width: 8,
                    height: 8,
                    backgroundColor: config.color
                  }}
                />
                <small className="text-muted">New</small>
              </div>
            )}
          </div>
        </div>
        
        {/* Action Buttons */}
        <Collapse in={showActions}>
          <div className="mt-3 pt-2 border-top">
            <div className="d-flex gap-2 flex-wrap">
              {availableActions.map(actionType => {
                const action = NOTIFICATION_ACTIONS[actionType];
                return (
                  <Button
                    key={actionType}
                    variant={action.variant}
                    size={action.size}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAction(actionType);
                    }}
                    className="d-flex align-items-center"
                  >
                    <FontAwesomeIcon 
                      icon={getIconComponent(action.icon)} 
                      className="me-1" 
                      size="xs"
                    />
                    {action.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </Collapse>
      </ListGroup.Item>
      
      {/* Group Items */}
      {notification.isGroup && (
        <Collapse in={showGroupItems}>
          <div>
            {notification.items?.slice(0, 5).map((item, index) => (
              <ListGroup.Item
                key={item.id || index}
                className="ps-5 border-start-0 bg-light small"
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span>{item.message}</span>
                  <small className="text-muted">
                    {formatNotificationTime(item.created_at)}
                  </small>
                </div>
              </ListGroup.Item>
            ))}
            
            {notification.items?.length > 5 && (
              <ListGroup.Item className="ps-5 border-start-0 bg-light text-center">
                <small className="text-muted">
                  +{notification.items.length - 5} more items
                </small>
              </ListGroup.Item>
            )}
          </div>
        </Collapse>
      )}
    </>
  );
};

export default NotificationItem;